import { AuthConsumer, AuthProvider } from '@dashboard/context/AuthContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import queryString from 'query-string';
import React, { useEffect } from 'react';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';

import ScrollToTop from '@/shared/components/ScrollToTop';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { PanelProvider } from '@dashboard/context/PanelContext';
import { TestProvider } from '@dashboard/context/TestContext';
import { ToastProvider } from '@dashboard/context/ToastContext';
import insertScript from '@dashboard/lib/insertScript';
import { isProduction } from '@dashboard/services/env';
import AuthLayout from '@dashboard/layouts/AuthLayout';
import MainLayout from '@dashboard/layouts/MainLayout';
import Account from '@dashboard/scenes/Account';
import BillingSettings from '@dashboard/scenes/BillingSettings';
import CustomerDetail from '@dashboard/scenes/CustomerDetail';
import Customers from '@dashboard/scenes/Customers';
import Dashboard from '@dashboard/scenes/Dashboard';
import DiscountsCoupons from '@dashboard/scenes/DiscountsCoupons';
import DiscountsCouponsCreate from '@dashboard/scenes/DiscountsCouponsCreate';
import DiscountsCouponsDetailOverview from '@dashboard/scenes/DiscountsCouponsDetail/DiscountsCouponsDetailOverview';
import IntegrationsGoogleCallback from '@dashboard/scenes/Integrations/GoogleCallback';
import IntegrationsSlackCallback from '@dashboard/scenes/Integrations/SlackCallback';
import Logout from '@dashboard/scenes/Logout';
import PageCreate from '@dashboard/scenes/PageCreate';
import PageEdit from '@dashboard/scenes/PageEdit';
import PageNew from '@dashboard/scenes/PageNew';
import PageOverview from '@dashboard/scenes/PageOverview';
import PageResults from '@dashboard/scenes/PageResults';
import Pages from '@dashboard/scenes/Pages';
import PaymentDuePage from '@dashboard/scenes/PaymentDue';
import PaymentsCharges from '@dashboard/scenes/PaymentsCharges';
import PaymentsChargesDetailOverview from '@dashboard/scenes/PaymentsChargesDetail/PaymentsChargesDetailOverview';
import PaymentsSubscriptionsDetailOverview from '@dashboard/scenes/PaymentsSubscriptionsDetail/PaymentsSubscriptionsDetailOverview';
import PaymentsSubscriptions from '@dashboard/scenes/PaymentsSubscriptions';
import SellerConnectStripe from '@dashboard/scenes/SellerConnectStripe';
import SellerStart from '@dashboard/scenes/SellerStart';
import Settings from '@dashboard/scenes/Settings';
import Submissions from '@dashboard/scenes/Submissions';
import SubmissionsDetailOverview from '@dashboard/scenes/SubmissionsDetail/SubmissionsDetailOverview';
import Updates from '@dashboard/scenes/Updates';
import UpgradePage from '@dashboard/scenes/UpgradePage';
import Admin from '@dashboard/admin/admin';
import NotFound from '@dashboard/scenes/NotFound';
import PublicLayout from '@dashboard/layouts/PublicLayout';
import { AdminGhostingProvider } from '@dashboard/context/AdminGhostingContext/AdminGhostingContext';
import PaymentsBookingsDetailOverview from '@/dashboard/scenes/PaymentsBookingsDetail';
import PaymentsBookings from '@/dashboard/scenes/PaymentsBookings';
import DiscountsCouponsDetailOverviewLabs from './scenes/DiscountsCouponsDetail/DiscountsCouponsDetailOverviewLabs';

// Create a client
const queryClient = new QueryClient();

const PrivateAppRoute = ({ component: Component, isFocused, isUpgradeRoute, ...rest }) => {
  const {
    isAuth,
    seller,
    hasActiveSubscription,
    isTrialing,
    hasPastDueSubscription,
    isAdminLoggedInAsSeller,
  } = useAuth();

  if (!isAuth) {
    return <Redirect to="login" />;
  }

  if (isUpgradeRoute && !isAdminLoggedInAsSeller) {
    if (isAuth && seller._id && !hasActiveSubscription && !isTrialing) {
      return <Redirect to="/upgrade" />;
    }

    if (isAuth && seller._id && hasPastDueSubscription) {
      return <Redirect to="/payment-due" />;
    }
  }

  return (
    <MainLayout {...rest} isFocused={isFocused}>
      <Route component={Component} isAuth={isAuth} {...rest} />
    </MainLayout>
  );
};

const PublicRoute = ({ component: Component, ...rest }) => (
  <PublicLayout {...rest}>
    <Route {...rest} render={matchProps => <Component {...matchProps} />} />
  </PublicLayout>
);

const DashboardApp = ({ children }) => {
  useEffect(() => {
    function loadHotjar() {
      insertScript(`
        (function(h,o,t,j,a,r){
            h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
            h._hjSettings={hjid:3393251,hjsv:6};
            a=o.getElementsByTagName('head')[0];
            r=o.createElement('script');r.async=1;
            r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
            a.appendChild(r);
        })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
      `);
    }

    if (isProduction) {
      loadHotjar();
    }
  }, []);

  return (
    <ToastProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider value={{ shouldAuthenticate: true }}>
          <TestProvider>
            <PanelProvider>
              <AdminGhostingProvider>{children}</AdminGhostingProvider>
            </PanelProvider>
          </TestProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ToastProvider>
  );
};

export default function dashboardRoutes() {
  return (
    <DashboardApp>
      <AuthConsumer>
        {({ isAuth, user, seller }) => {
          if (!isAuth) {
            const currentPath = window.location.pathname;
            const currentSearch = window.location.search;
            const loginUrl = queryString.stringifyUrl({
              url: '/login',
              query: {
                ...queryString.parse(currentSearch),
                redirect: currentPath,
              },
            });

            return <Redirect to={loginUrl} />;
          }

          const basename = '/dashboard';

          return (
            <BrowserRouter basename={basename}>
              <ScrollToTop />
              <Switch>
                <PrivateAppRoute path="/logout" component={Logout} isAuth={isAuth} user={user} />

                <PrivateAppRoute
                  isUpgradeRoute
                  path="/"
                  exact
                  component={Dashboard}
                  isAuth={isAuth}
                  user={user}
                />

                <PrivateAppRoute
                  isUpgradeRoute
                  path={`/pages/new`}
                  component={PageNew}
                  isAuth={isAuth}
                  user={user}
                  isFocused
                />
                <PrivateAppRoute
                  isUpgradeRoute
                  path={`/pages/create`}
                  component={PageCreate}
                  isAuth={isAuth}
                  user={user}
                  isFocused
                />
                <PrivateAppRoute
                  isUpgradeRoute
                  path={`/pages/:id/results`}
                  component={PageResults}
                  isAuth={isAuth}
                  user={user}
                />
                <PrivateAppRoute
                  isUpgradeRoute
                  path={`/pages/:id/customize`}
                  component={PageEdit}
                  isAuth={isAuth}
                  user={user}
                  isFocused
                />
                <PrivateAppRoute
                  isUpgradeRoute
                  path={`/pages/:id`}
                  component={PageOverview}
                  isAuth={isAuth}
                  user={user}
                  isFocused
                />

                <PrivateAppRoute
                  isUpgradeRoute
                  path="/pages"
                  component={Pages}
                  isAuth={isAuth}
                  user={user}
                />

                <PrivateAppRoute
                  isUpgradeRoute
                  path="/orders/payments/:chargeId"
                  component={PaymentsChargesDetailOverview}
                  isAuth={isAuth}
                  user={user}
                />

                <PrivateAppRoute
                  isUpgradeRoute
                  path="/orders/bookings/:chargeId"
                  component={PaymentsBookingsDetailOverview}
                  isAuth={isAuth}
                  user={user}
                />

                <PrivateAppRoute
                  isUpgradeRoute
                  path="/orders/subscriptions/:subscriptionId"
                  component={PaymentsSubscriptionsDetailOverview}
                  isAuth={isAuth}
                  user={user}
                />

                <PrivateAppRoute
                  isUpgradeRoute
                  path="/orders/payments"
                  component={PaymentsCharges}
                  isAuth={isAuth}
                  user={user}
                />

                <PrivateAppRoute
                  isUpgradeRoute
                  path="/orders/bookings"
                  component={PaymentsBookings}
                  isAuth={isAuth}
                  user={user}
                />

                <PrivateAppRoute
                  isUpgradeRoute
                  path="/orders/subscriptions"
                  component={PaymentsSubscriptions}
                  isAuth={isAuth}
                  user={user}
                />

                <PrivateAppRoute
                  isUpgradeRoute
                  exact
                  path="/submissions"
                  component={Submissions}
                  isAuth={isAuth}
                  user={user}
                />

                <PrivateAppRoute
                  isUpgradeRoute
                  path="/submissions/:submissionId"
                  component={SubmissionsDetailOverview}
                  isAuth={isAuth}
                  user={user}
                />

                <PrivateAppRoute
                  isUpgradeRoute
                  path="/customers"
                  exact
                  component={Customers}
                  isAuth={isAuth}
                  user={user}
                />
                <PrivateAppRoute
                  isUpgradeRoute
                  path="/customers/:id"
                  exact
                  component={CustomerDetail}
                  isAuth={isAuth}
                  user={user}
                />

                <PrivateAppRoute
                  isUpgradeRoute
                  path="/coupons"
                  exact
                  component={DiscountsCoupons}
                  isAuth={isAuth}
                  user={user}
                />
                <PrivateAppRoute
                  isUpgradeRoute
                  path="/coupons/new"
                  exact
                  component={DiscountsCouponsCreate}
                  isAuth={isAuth}
                  user={user}
                  isFocused
                />

                <PrivateAppRoute
                  isUpgradeRoute
                  path="/coupons/:id"
                  component={
                    seller?.labs?.couponTicketTypes
                      ? DiscountsCouponsDetailOverviewLabs
                      : DiscountsCouponsDetailOverview
                  }
                  isAuth={isAuth}
                  user={user}
                />

                <PrivateAppRoute
                  path="/settings"
                  component={Settings}
                  isAuth={isAuth}
                  user={user}
                />

                <PrivateAppRoute path="/account" component={Account} isAuth={isAuth} user={user} />
                <PrivateAppRoute
                  path="/billing"
                  component={BillingSettings}
                  isAuth={isAuth}
                  user={user}
                />

                <PrivateAppRoute path="/updates" component={Updates} isAuth={isAuth} user={user} />

                <PrivateAppRoute
                  path="/upgrade"
                  component={UpgradePage}
                  isAuth={isAuth}
                  user={user}
                />

                <PrivateAppRoute
                  path="/payment-due"
                  component={PaymentDuePage}
                  isAuth={isAuth}
                  user={user}
                />

                <Route path="/seller/start" component={SellerStart} />

                <Route path="/seller/connect-stripe">
                  <AuthLayout>
                    <SellerConnectStripe />
                  </AuthLayout>
                </Route>

                <Route path="/integrations/google/callback">
                  <AuthLayout>
                    <IntegrationsGoogleCallback />
                  </AuthLayout>
                </Route>

                <Route path="/integrations/slack/callback">
                  <AuthLayout>
                    <IntegrationsSlackCallback />
                  </AuthLayout>
                </Route>

                <Route path="/admin" component={Admin} />

                <PublicRoute component={NotFound} />
                <PublicRoute path="/404" component={NotFound} />
              </Switch>
            </BrowserRouter>
          );
        }}
      </AuthConsumer>
    </DashboardApp>
  );
}
